import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { toast } from 'sonner';
import { Building2, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, sendOTP, loginWithOTP } = useAuth();
  
  const [loginMethod, setLoginMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  
  const [emailForm, setEmailForm] = useState({ email: '', password: '' });
  const [phoneForm, setPhoneForm] = useState({ phone: '', password: '' });
  const [otpForm, setOtpForm] = useState({ phone: '', otp: '' });

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login({ email: emailForm.email, password: emailForm.password });
      toast.success(`Welcome back, ${user.name}!`);
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login({ phone: phoneForm.phone, password: phoneForm.password });
      toast.success(`Welcome back, ${user.name}!`);
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!otpForm.phone) {
      toast.error('Please enter phone number');
      return;
    }
    setLoading(true);
    try {
      const result = await sendOTP(otpForm.phone);
      setOtpSent(true);
      toast.success('OTP sent to your phone');
      // For demo, show OTP
      if (result.otp) {
        toast.info(`Demo OTP: ${result.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPLogin = async () => {
    if (otpForm.otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }
    setLoading(true);
    try {
      const user = await loginWithOTP(otpForm.phone, otpForm.otp);
      toast.success(`Welcome back, ${user.name}!`);
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (role) => {
    const routes = {
      'super_admin': '/admin',
      'builder_admin': '/builder',
      'sales_team': '/sales',
      'marketing_team': '/marketing',
      'partner': '/partner',
      'sub_user': '/partner',
    };
    navigate(routes[role] || '/');
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Hero image */}
      <div 
        className="hidden md:flex md:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1764996915324-91919cee14d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmRpYW4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDE5NTU1OXww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="h-10 w-10 text-amber-500" />
            <span className="text-3xl font-bold tracking-tight">effyRealty</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            Empower Your Real Estate Business
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Manage channel partners, leads, commissions, and microsites all in one powerful platform.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 md:hidden">
            <Building2 className="h-8 w-8 text-amber-600" />
            <span className="text-2xl font-bold">effyRealty</span>
          </div>

          <Card className="border-border">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold" data-testid="login-title">Welcome back</CardTitle>
              <CardDescription>
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={loginMethod} onValueChange={setLoginMethod}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="email" data-testid="tab-email">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </TabsTrigger>
                  <TabsTrigger value="phone" data-testid="tab-phone">
                    <Phone className="h-4 w-4 mr-2" />
                    Phone
                  </TabsTrigger>
                  <TabsTrigger value="otp" data-testid="tab-otp">
                    <Lock className="h-4 w-4 mr-2" />
                    OTP
                  </TabsTrigger>
                </TabsList>

                {/* Email Login */}
                <TabsContent value="email">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@example.com"
                        value={emailForm.email}
                        onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={emailForm.password}
                          onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                          required
                          data-testid="input-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800" 
                      disabled={loading}
                      data-testid="btn-login-email"
                    >
                      {loading ? 'Signing in...' : 'Sign in'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Phone Login */}
                <TabsContent value="phone">
                  <form onSubmit={handlePhoneLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+91 9876543210"
                        value={phoneForm.phone}
                        onChange={(e) => setPhoneForm({ ...phoneForm, phone: e.target.value })}
                        required
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="phone-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={phoneForm.password}
                          onChange={(e) => setPhoneForm({ ...phoneForm, password: e.target.value })}
                          required
                          data-testid="input-phone-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800" 
                      disabled={loading}
                      data-testid="btn-login-phone"
                    >
                      {loading ? 'Signing in...' : 'Sign in'}
                    </Button>
                  </form>
                </TabsContent>

                {/* OTP Login */}
                <TabsContent value="otp">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-phone">Phone Number</Label>
                      <div className="flex gap-2">
                        <Input
                          id="otp-phone"
                          type="tel"
                          placeholder="+91 9876543210"
                          value={otpForm.phone}
                          onChange={(e) => setOtpForm({ ...otpForm, phone: e.target.value })}
                          disabled={otpSent}
                          data-testid="input-otp-phone"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleSendOTP}
                          disabled={loading || otpSent}
                          data-testid="btn-send-otp"
                        >
                          {otpSent ? 'Sent' : 'Send OTP'}
                        </Button>
                      </div>
                    </div>

                    {otpSent && (
                      <div className="space-y-2 animate-fade-in">
                        <Label>Enter OTP</Label>
                        <InputOTP
                          maxLength={6}
                          value={otpForm.otp}
                          onChange={(value) => setOtpForm({ ...otpForm, otp: value })}
                          data-testid="input-otp"
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                        <Button 
                          className="w-full h-12 mt-4 bg-slate-900 hover:bg-slate-800" 
                          onClick={handleOTPLogin}
                          disabled={loading}
                          data-testid="btn-verify-otp"
                        >
                          {loading ? 'Verifying...' : 'Verify & Sign in'}
                        </Button>
                        <button
                          type="button"
                          className="w-full text-sm text-muted-foreground hover:text-foreground"
                          onClick={() => { setOtpSent(false); setOtpForm({ ...otpForm, otp: '' }); }}
                        >
                          Change phone number
                        </button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">Don't have an account? </span>
                <Link to="/register" className="text-amber-600 hover:text-amber-700 font-medium" data-testid="link-register">
                  Register as Partner
                </Link>
              </div>

              {/* Demo Credentials */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-3 text-center">DEMO CREDENTIALS (6 User Types)</p>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <DemoCredential role="Super Admin" email="superadmin@effy.com" password="admin123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                  <DemoCredential role="Builder Admin" email="builder@effy.com" password="builder123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                  <DemoCredential role="Sales Team" email="sales@effy.com" password="sales123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                  <DemoCredential role="Marketing" email="marketing@effy.com" password="marketing123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                  <DemoCredential role="Partner" email="partner@effy.com" password="partner123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                  <DemoCredential role="Sub-User" email="staff@effy.com" password="staff123" onClick={(e,p) => { setEmailForm({email: e, password: p}); setLoginMethod('email'); }} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const DemoCredential = ({ role, email, password, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(email, password)}
    className="flex items-center justify-between p-2 rounded bg-muted/50 hover:bg-muted transition-colors text-left w-full"
    data-testid={`demo-${role.toLowerCase().replace(/\s/g, '-')}`}
  >
    <span className="font-medium text-foreground">{role}</span>
    <span className="text-muted-foreground">{email}</span>
  </button>
);

export default LoginPage;
